import { useState, useEffect } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { apiFetch } from '@/lib/apiFetch'
import MagnumSays from '@/components/MagnumSays'
import TvPauseAutoOff from '@/components/TvPauseAutoOff'
import ActInsightsPanel from '@/components/ActInsightsPanel'
import { cn } from '@/lib/utils'

interface ChildInfo {
  id: number
  displayName: string
  username: string
  birthDate: number | null
}

interface ChildStats {
  child: ChildInfo
  totalAttempts: number
  correctAttempts: number
  accuracy: number
  uniqueWordsAttempted: number
  totalPracticeMinutes: number
  totalSessions: number
  sessionsThisWeek: number
  attemptsThisWeek: number
  correctThisWeek: number
  practiceMinutesThisWeek: number
  attemptsToday: number
  correctToday: number
}

interface DashboardData {
  children: ChildStats[]
  totalWords: number
}

interface DailyStats {
  date: string
  practiceMinutes: number
  sessions: number
  attempts: number
  correct: number
  accuracy: number
}

interface GoalEntry {
  goal: {
    id: number
    name: string
    type: string
    goalValue: number
    period: string
    currentProgress: number
    currentStreak: number
    longestStreak: number
    status: string
    listId: number | null
  }
  streakRewards: { id: number; streakLength: number; rewardText: string | null; status: string }[]
}

interface Tv {
  id: number
  name: string
  tvType: string
  mode: string
  autoOffOverrideUntil: number | null
}

interface TvStatus {
  authorized: boolean
  summary: string
  reason: string
  minutesLeft?: number
  childName?: string
}

interface SessionSummary {
  session: { id: number; startedAt: number; endedAt: number | null; exerciseType: string | null }
  totalAttempts: number
  correctAttempts: number
  accuracy: number
  durationMs: number
}

function exerciseLabel(type: string | null): { emoji: string; name: string } {
  switch (type) {
    case 'WORD_DECODE': return { emoji: '🎤', name: 'Word Decode' }
    case 'SYNONYM_MATCH': return { emoji: '🔤', name: 'Synonym Match' }
    case 'SYNONYM_PAIRS': return { emoji: '🧩', name: 'Synonym Pairs' }
    case 'SPELLING': return { emoji: '📝', name: 'Spelling' }
    case 'NOTE_NAMING': return { emoji: '🎵', name: 'Note Naming' }
    case 'INTERVAL_RECOGNITION': return { emoji: '🎼', name: 'Interval Recognition' }
    case 'EAR_TRAINING': return { emoji: '👂', name: 'Ear Training' }
    default: return { emoji: '🎤', name: 'Practice' }
  }
}

function formatDuration(ms: number): string {
  const m = Math.floor(ms / 60000)
  const s = Math.floor((ms % 60000) / 1000)
  return m > 0 ? `${m}m ${s}s` : `${s}s`
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
}

function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

// --- Bar chart ---

function PracticeChart({ data }: { data: DailyStats[] }) {
  const maxMin = Math.max(...data.map(d => d.practiceMinutes), 1)
  const last30 = data.slice(-30)

  // Pick ~5 evenly spaced label positions
  const labelIndices = new Set<number>()
  if (last30.length > 0) {
    labelIndices.add(0)
    labelIndices.add(last30.length - 1)
    const step = Math.floor(last30.length / 4)
    for (let i = step; i < last30.length - 1; i += step) labelIndices.add(i)
  }

  const todayStr = new Date().toISOString().slice(0, 10)

  return (
    <div className="space-y-1">
      <h2 className="text-lg font-semibold">Practice Time</h2>
      <div className="flex items-end gap-[2px] h-32">
        {last30.map(d => {
          const h = d.practiceMinutes > 0 ? Math.max(4, (d.practiceMinutes / maxMin) * 100) : 0
          return (
            <div key={d.date} className="flex-1 flex flex-col items-center justify-end h-full group relative">
              <div
                className={cn(
                  'w-full rounded-t transition-colors',
                  d.practiceMinutes > 0 ? 'bg-primary hover:bg-primary/80' : 'bg-transparent'
                )}
                style={{ height: `${h}%`, minHeight: d.practiceMinutes > 0 ? 4 : 0 }}
                title={`${formatDate(d.date)}: ${d.practiceMinutes} min`}
              />
              <div className="absolute bottom-full mb-1 hidden group-hover:block z-10 pointer-events-none">
                <div className="bg-foreground text-background text-xs rounded px-2 py-1 whitespace-nowrap">
                  {formatDate(d.date)}: {d.practiceMinutes} min
                </div>
              </div>
            </div>
          )
        })}
      </div>
      <div className="flex text-[10px] text-muted-foreground">
        {last30.map((d, i) => (
          <span key={d.date} className="flex-1 text-center truncate">
            {labelIndices.has(i)
              ? d.date === todayStr
                ? 'Today'
                : new Date(d.date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
              : ''}
          </span>
        ))}
      </div>
    </div>
  )
}

// --- Goals section ---

function GoalsTracker({ goals, childId }: { goals: GoalEntry[]; childId: number }) {
  const active = goals.filter(e => e.goal.status === 'ACTIVE')
  if (active.length === 0) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Goals</h2>
          <Link to={`/dashboard/children/${childId}/goals`}
            className="text-xs text-primary hover:underline">Manage</Link>
        </div>
        <p className="text-sm text-muted-foreground">No active goals. Set one up to track progress!</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Goals</h2>
        <Link to={`/dashboard/children/${childId}/goals`}
          className="text-xs text-primary hover:underline">Manage</Link>
      </div>
      <div className="grid gap-2">
        {active.map(({ goal }) => {
          const pct = goal.goalValue > 0 ? Math.min(100, Math.round((goal.currentProgress / goal.goalValue) * 100)) : 0
          const unit = goal.type === 'DURATION' ? 'min' : 'words'
          const periodStr = goal.type === 'LIST_MASTERY' ? '' : (goal.period === 'WEEKLY' ? '/wk' : '/day')
          return (
            <div key={goal.id} className="p-3 border border-border rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">{goal.name}</span>
                {goal.currentStreak > 0 && (
                  <span className="text-xs text-amber-500 font-medium">🔥 {goal.currentStreak}</span>
                )}
              </div>
              <div className="w-full bg-accent rounded-full h-2 overflow-hidden">
                <div className="bg-primary h-full rounded-full transition-all duration-500"
                  style={{ width: `${pct}%` }} />
              </div>
              <p className="text-xs text-muted-foreground">
                {goal.currentProgress} / {goal.goalValue} {unit}{periodStr}
                {goal.longestStreak > 1 && <span className="ml-2">Best streak: {goal.longestStreak}</span>}
              </p>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// --- Daily feed ---

function DailyFeed({ days, childId }: { days: DailyStats[]; childId: number }) {
  const withActivity = days.filter(d => d.sessions > 0).reverse()

  if (withActivity.length === 0) {
    return null
  }

  return (
    <div className="space-y-2">
      <h2 className="text-lg font-semibold">Daily Activity</h2>
      <div className="space-y-1">
        {withActivity.map(d => (
          <DayRow key={d.date} day={d} childId={childId} />
        ))}
      </div>
    </div>
  )
}

function DayRow({ day, childId }: { day: DailyStats; childId: number }) {
  const [expanded, setExpanded] = useState(false)
  const [sessions, setSessions] = useState<SessionSummary[] | null>(null)
  const [loading, setLoading] = useState(false)

  const handleExpand = async () => {
    if (expanded) { setExpanded(false); return }
    setExpanded(true)
    if (sessions) return
    setLoading(true)
    try {
      // Fetch sessions for this day — use date range
      const startOfDay = new Date(day.date + 'T00:00:00').getTime()
      const endOfDay = startOfDay + 24 * 60 * 60 * 1000
      const data = await apiFetch<{ sessions: SessionSummary[]; total: number }>(
        `/dashboard/children/${childId}/sessions?limit=50&offset=0`
      )
      const daySessions = data.sessions.filter(
        s => s.session.startedAt >= startOfDay && s.session.startedAt < endOfDay
      )
      setSessions(daySessions)
    } catch {
      setSessions([])
    } finally {
      setLoading(false)
    }
  }

  const accuracy = day.attempts > 0 ? Math.round(day.accuracy * 100) : null

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        onClick={handleExpand}
        className="w-full p-3 flex items-center justify-between text-left hover:bg-accent/50 transition min-h-[44px]"
      >
        <span className="text-sm font-medium">{formatDate(day.date)}</span>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span>{day.practiceMinutes} min</span>
          <span>{day.sessions} {day.sessions === 1 ? 'session' : 'sessions'}</span>
          {accuracy !== null && <span>{accuracy}%</span>}
          <span className="text-muted-foreground">{expanded ? '▲' : '▼'}</span>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-border bg-muted/30 px-3 py-2">
          {loading && <p className="text-xs text-muted-foreground py-2">Loading...</p>}
          {sessions && sessions.length === 0 && (
            <p className="text-xs text-muted-foreground py-2">No sessions found.</p>
          )}
          {sessions && sessions.length > 0 && (
            <div className="space-y-1">
              {sessions.map(s => {
                const { emoji, name } = exerciseLabel(s.session.exerciseType)
                return (
                  <Link
                    key={s.session.id}
                    to={`/dashboard/children/${childId}/activity/${s.session.id}`}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-accent transition text-sm"
                  >
                    <div className="flex items-center gap-2">
                      <span>{emoji}</span>
                      <span className="font-medium">{name}</span>
                      <span className="text-xs text-muted-foreground">
                        {formatTime(s.session.startedAt)}
                        {s.session.endedAt && <> – {formatTime(s.session.endedAt)}</>}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span>{formatDuration(s.durationMs)}</span>
                      <span>{Math.round(s.accuracy * 100)}%</span>
                      <span>{s.totalAttempts} attempts</span>
                    </div>
                  </Link>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// --- Allowance signoff (homework check) ---

interface SignoffStatus {
  required: boolean
  approved: boolean
  approvedByName?: string | null
  approvedAt?: number
}

function SignoffCard({ childId }: { childId: number }) {
  const [status, setStatus] = useState<SignoffStatus | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setStatus(null)
    apiFetch<SignoffStatus>(`/screen-time/signoff?childId=${childId}`).then(setStatus).catch(() => {})
  }, [childId])

  const update = async (method: 'POST' | 'DELETE') => {
    setSaving(true)
    try {
      const result = await apiFetch<SignoffStatus>(`/screen-time/signoff?childId=${childId}`, { method })
      setStatus(result)
    } catch {}
    finally { setSaving(false) }
  }

  if (!status?.required) return null

  return status.approved ? (
    <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-green-500/10 border border-green-500/20">
      <span className="text-xs font-medium text-green-700 dark:text-green-400">
        ✅ Screen time signed off for today
        {status.approvedByName && <> by {status.approvedByName}</>}
        {status.approvedAt && <> at {formatTime(status.approvedAt)}</>}
      </span>
      <button onClick={() => update('DELETE')} disabled={saving}
        className="text-xs text-green-700 dark:text-green-400 font-semibold hover:underline shrink-0">
        Undo
      </button>
    </div>
  ) : (
    <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
      <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
        📝 Homework check needed before today's screen time
      </span>
      <button onClick={() => update('POST')} disabled={saving}
        className="text-xs px-3 py-1.5 bg-primary text-primary-foreground rounded-md font-semibold hover:opacity-90 transition disabled:opacity-50 shrink-0 min-h-[36px]">
        {saving ? 'Approving...' : 'Approve'}
      </button>
    </div>
  )
}

// --- TVs tab ---

function TvStatusCard({ tv, onUpdate }: { tv: Tv; onUpdate: () => void }) {
  const [status, setStatus] = useState<TvStatus | null>(null)

  useEffect(() => {
    const load = () => {
      apiFetch<TvStatus>(`/tvs/${tv.id}/status`).then(setStatus).catch(() => {})
    }
    load()
    const interval = setInterval(load, 30000)
    return () => clearInterval(interval)
  }, [tv.id, tv.mode, tv.autoOffOverrideUntil])

  return (
    <div className="border border-border rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold">{tv.name}</span>
        <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{tv.tvType.replace('_', ' ')}</span>
      </div>

      {status && (
        <div className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium',
          status.authorized
            ? 'bg-green-500/10 border border-green-500/20 text-green-700 dark:text-green-400'
            : 'bg-red-500/10 border border-red-500/20 text-red-700 dark:text-red-400'
        )}>
          <span className={cn(
            'w-2 h-2 rounded-full shrink-0',
            status.authorized ? 'bg-green-500' : 'bg-red-500'
          )} />
          {status.summary}
        </div>
      )}

      <div className="pt-2 border-t border-border">
        <TvPauseAutoOff tvId={tv.id} autoOffOverrideUntil={tv.autoOffOverrideUntil} onUpdate={onUpdate} />
      </div>
    </div>
  )
}

function TvsPanel({ tvs, onUpdate }: { tvs: Tv[]; onUpdate: () => void }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">TVs</h2>
        <Link to="/settings/tvs" className="text-xs text-primary hover:underline">TV Settings</Link>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {tvs.map(tv => (
          <TvStatusCard key={tv.id} tv={tv} onUpdate={onUpdate} />
        ))}
      </div>
    </div>
  )
}

// --- Main page ---

export default function DashboardPage() {
  const { user } = useAuth()
  const history = useHistory()
  const [dashboard, setDashboard] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedChildId, setSelectedChildId] = useState<number | null>(null)
  const [dailyStats, setDailyStats] = useState<DailyStats[]>([])
  const [goals, setGoals] = useState<GoalEntry[]>([])
  const [dailyLoading, setDailyLoading] = useState(false)
  const [tvs, setTvs] = useState<Tv[]>([])
  const [showTvs, setShowTvs] = useState(false)
  const familyId = user?.type === 'parent' ? user.families?.[0]?.id : null

  // Load TVs for the TVs tab
  const loadTvs = () => {
    if (!familyId) return
    apiFetch<Tv[]>(`/tvs?familyId=${familyId}`).then(setTvs).catch(() => {})
  }
  useEffect(() => { loadTvs() }, [familyId])

  // Load main dashboard data
  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const data = await apiFetch<DashboardData>('/dashboard')
        if (!cancelled) {
          setDashboard(data)
          if (data.children.length > 0 && !selectedChildId) {
            setSelectedChildId(data.children[0].child.id)
          }
        }
      } catch {}
      finally { if (!cancelled) setLoading(false) }
    }
    load()
    return () => { cancelled = true }
  }, [])

  // Load child-specific data when selection changes
  useEffect(() => {
    if (!selectedChildId) return
    let cancelled = false
    setDailyLoading(true)

    Promise.all([
      apiFetch<DailyStats[]>(`/dashboard/children/${selectedChildId}/daily-stats?tz=${encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone)}`),
      apiFetch<{ goals: GoalEntry[] }>(`/goals/full?childId=${selectedChildId}`),
    ]).then(([daily, goalsData]) => {
      if (!cancelled) {
        setDailyStats(daily)
        setGoals(goalsData.goals || [])
      }
    }).catch(() => {}).finally(() => {
      if (!cancelled) setDailyLoading(false)
    })

    return () => { cancelled = true }
  }, [selectedChildId])

  if (!user || user.type !== 'parent') return null

  const { parent, children } = user

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <p className="text-muted-foreground">Loading dashboard...</p>
      </div>
    )
  }

  if (children.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
        <h1 className="text-2xl font-bold">Welcome, {parent.firstName}!</h1>
        <div className="py-8 space-y-4 flex flex-col items-center">
          <MagnumSays>
            <p className="font-medium">Hey there! I'm Tim.</p>
            <p className="mt-1 text-muted-foreground">
              Add your first reader and we'll get started on a reading adventure together!
            </p>
          </MagnumSays>
          <Link
            to="/settings"
            className="inline-block px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:opacity-90 transition"
          >
            Add a Reader
          </Link>
        </div>
      </div>
    )
  }

  const selectedStats = dashboard?.children.find(c => c.child.id === selectedChildId)
  const selectedChild = children.find(c => c.id === selectedChildId)

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      {/* Kid picker + TVs tab */}
      {children.length > 1 || tvs.length > 0 ? (
        <div className="flex items-center gap-2 flex-wrap">
          {children.map(c => (
            <button
              key={c.id}
              onClick={() => { setShowTvs(false); setSelectedChildId(c.id) }}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium transition min-h-[44px]',
                !showTvs && c.id === selectedChildId
                  ? 'bg-primary text-primary-foreground'
                  : 'border border-border text-muted-foreground hover:bg-accent'
              )}
            >
              {c.displayName}
            </button>
          ))}
          {tvs.length > 0 && (
            <button
              onClick={() => setShowTvs(true)}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium transition min-h-[44px]',
                showTvs
                  ? 'bg-primary text-primary-foreground'
                  : 'border border-border text-muted-foreground hover:bg-accent'
              )}
            >
              📺 TVs
            </button>
          )}
        </div>
      ) : (
        <h1 className="text-2xl font-bold">{selectedChild?.displayName}</h1>
      )}

      {showTvs ? (
        <TvsPanel tvs={tvs} onUpdate={loadTvs} />
      ) : (
        <>
          {/* Homework signoff (only renders when the family setting is on) */}
          {selectedChildId && <SignoffCard childId={selectedChildId} />}

          {/* Quick links */}
          <div className="flex gap-2 flex-wrap">
            <Link to={`/dashboard/children/${selectedChildId}/goals`}
              className="text-xs px-3 py-1.5 border border-border rounded-md hover:bg-accent transition min-h-[36px] inline-flex items-center">
              Goals & Rewards
            </Link>
            <Link to={`/dashboard/children/${selectedChildId}/game-settings`}
              className="text-xs px-3 py-1.5 border border-border rounded-md hover:bg-accent transition min-h-[36px] inline-flex items-center">
              Game Settings
            </Link>
            <Link to={`/dashboard/children/${selectedChildId}/screen-time`}
              className="text-xs px-3 py-1.5 border border-border rounded-md hover:bg-accent transition min-h-[36px] inline-flex items-center">
              Screen Time
            </Link>
            <Link to={`/dashboard/children/${selectedChildId}/activity`}
              className="text-xs px-3 py-1.5 border border-border rounded-md hover:bg-accent transition min-h-[36px] inline-flex items-center">
              Full Activity Log
            </Link>
          </div>

          {dailyLoading ? (
            <p className="text-muted-foreground text-sm py-8 text-center">Loading stats...</p>
          ) : (
            <>
              {/* 30-day chart */}
              {dailyStats.length > 0 && <PracticeChart data={dailyStats} />}

              {/* Goals & streaks */}
              {selectedChildId && <GoalsTracker goals={goals} childId={selectedChildId} />}

              {/* ACT estimation & prediction rollup */}
              {selectedChildId && <ActInsightsPanel childId={selectedChildId} />}

              {/* Daily feed */}
              {selectedChildId && <DailyFeed days={dailyStats} childId={selectedChildId} />}
            </>
          )}
        </>
      )}
    </div>
  )
}
